import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { ApprovalContext } from '@/features/session/types'

/**
 * Open state for the "Discuss this Execution" modal.
 *
 * Holds only the identifiers needed to open the modal — the heavy lifting
 * (loading the execution, fanning out across agent-session transcripts,
 * assembling the markdown context blob) lives inside the modal's own
 * `useDiscussContext` hook so this slice stays tiny.
 *
 * `attentionItemId` is optional because the same modal can be opened from
 * the Execution Detail page where no AttentionItem exists; queue surfaces
 * pass it through so the markdown context can quote the approval id.
 */
export interface DiscussModalState {
  open: boolean
  executionId: string | null
  attentionItemId: string | null
}

interface UIState {
  sessionPanelOpen: boolean
  sidebarCollapsed: boolean
  commandPaletteOpen: boolean
  /**
   * Pending context to pass to session panel.
   * Set when "Ask Agent About This" is clicked; cleared when session panel processes it.
   */
  pendingApprovalContext: ApprovalContext | null
  /**
   * Whether the Local Claude panel is open.
   * Added by Task 3100b — the second right-panel slot.
   */
  claudePanelOpen: boolean
  /**
   * Which right panel is currently active in the tab strip.
   * 'stream' = Stream agents (SessionPanel), 'claude' = Local Claude (ClaudeSessionPanel).
   * Switches automatically when toggling a panel that is not yet active.
   */
  activeRightPanel: 'stream' | 'claude'
  /**
   * Open state for the "Discuss this Execution" modal. Transient — never
   * persisted to localStorage because reopening Stream should not auto-open
   * a debugging modal from a previous session.
   */
  discussModal: DiscussModalState
}

interface UIActions {
  toggleSessionPanel: () => void
  setSessionPanelOpen: (open: boolean) => void
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  setCommandPaletteOpen: (open: boolean) => void
  /**
   * Open session panel with approval context.
   * Opens the panel (if closed) and sets pending context for the session panel to pick up.
   */
  openSessionPanelWithContext: (context: ApprovalContext) => void
  /** Clear pending approval context after session panel has processed it. */
  clearPendingApprovalContext: () => void
  /**
   * Toggle the Local Claude panel.
   * If the panel is closed, also sets activeRightPanel to 'claude'.
   * Added by Task 3100b.
   */
  toggleClaudePanel: () => void
  /** Directly set the Local Claude panel open state. */
  setClaudePanelOpen: (open: boolean) => void
  /** Switch which right panel is active in the tab strip. */
  setActiveRightPanel: (panel: 'stream' | 'claude') => void
  /**
   * Open the "Discuss this Execution" modal for a given execution.
   *
   * Idempotent: calling with the same `executionId` while the modal is
   * already open is a no-op, so prefetch handlers and click handlers can
   * both invoke it freely. Calling with a different `executionId` replaces
   * the target — the modal swaps to the new execution context.
   */
  openDiscussModal: (input: {
    executionId: string
    attentionItemId?: string | null
  }) => void
  /** Close the Discuss modal. Conversation state in `useDiscussStore` is left
   * intact so reopening the same execution resumes the chat. */
  closeDiscussModal: () => void
}

export const useUIStore = create<UIState & UIActions>()(
  persist(
    (set) => ({
      sessionPanelOpen: false,
      sidebarCollapsed: false,
      commandPaletteOpen: false,
      pendingApprovalContext: null,
      claudePanelOpen: false,
      activeRightPanel: 'stream',
      discussModal: {
        open: false,
        executionId: null,
        attentionItemId: null,
      },

      toggleSessionPanel: () => set((s) => ({
        sessionPanelOpen: !s.sessionPanelOpen,
        // When opening the stream panel, switch the tab strip to it
        ...(s.sessionPanelOpen ? {} : { activeRightPanel: 'stream' as const }),
      })),
      setSessionPanelOpen: (open) => set({ sessionPanelOpen: open }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      setCommandPaletteOpen: (open) => set({ commandPaletteOpen: open }),

      openSessionPanelWithContext: (context) => {
        set({
          pendingApprovalContext: context,
          sessionPanelOpen: true,
          activeRightPanel: 'stream',
        })
      },

      clearPendingApprovalContext: () => {
        set({ pendingApprovalContext: null })
      },

      toggleClaudePanel: () => set((s) => ({
        claudePanelOpen: !s.claudePanelOpen,
        // When opening the Claude panel, switch the tab strip to it
        ...(s.claudePanelOpen ? {} : { activeRightPanel: 'claude' as const }),
      })),
      setClaudePanelOpen: (open) => set({ claudePanelOpen: open }),
      setActiveRightPanel: (panel) => set({ activeRightPanel: panel }),

      openDiscussModal: ({ executionId, attentionItemId }) =>
        set((s) => {
          // Idempotent re-entry: same execution already open → no-op.
          // (Prefetch handlers may call this on hover; we don't want
          // hover thrash to reset modal-local state.)
          if (
            s.discussModal.open &&
            s.discussModal.executionId === executionId
          ) {
            return s
          }
          return {
            discussModal: {
              open: true,
              executionId,
              attentionItemId: attentionItemId ?? null,
            },
          }
        }),

      closeDiscussModal: () =>
        set((s) => ({
          discussModal: {
            // Preserve the executionId/attentionItemId on close so the
            // modal can show its tear-down animation with the old data.
            // Open flag flipping false is what actually hides it.
            ...s.discussModal,
            open: false,
          },
        })),
    }),
    {
      name: 'stream-ui',
      storage: createJSONStorage(() => localStorage),
      // Only persist preference-style state; transient UI flags stay in memory.
      partialize: (state) => ({
        sessionPanelOpen: state.sessionPanelOpen,
        sidebarCollapsed: state.sidebarCollapsed,
        claudePanelOpen: state.claudePanelOpen,
        activeRightPanel: state.activeRightPanel,
        // Don't persist commandPaletteOpen or pendingApprovalContext
      }),
    }
  )
)
